import { useEffect, useRef } from "react"
import type { FaceLandmarker } from "@mediapipe/tasks-vision"

import { drawFaceMesh, type FaceMeshCapture } from "../face-mesh"

export interface LiveFaceMesh {
  /** Attach to a <canvas> overlaying the video at the same box. */
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  /**
   * The most recent detection from the live loop. Read `.current` on capture to
   * snapshot the exact frame the user saw — no second `detectForVideo`, which
   * would break MediaPipe's monotonic-timestamp requirement.
   */
  lastCaptureRef: React.RefObject<FaceMeshCapture | null>
}

/**
 * Runs a requestAnimationFrame loop while `active`, detecting the face in the
 * playing <video> and drawing the mesh onto the returned <canvas> (mirrored to
 * match the selfie view). Stores each frame's result so a capture can reuse it.
 * Stops and releases the loop when inactive or unmounted.
 */
export function useLiveFaceMesh(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  landmarker: FaceLandmarker | null,
  active: boolean,
): LiveFaceMesh {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const lastCaptureRef = useRef<FaceMeshCapture | null>(null)

  useEffect(() => {
    if (!active || !landmarker) return

    let raf = 0
    let errorLogged = false
    const loop = () => {
      raf = requestAnimationFrame(loop)
      const video = videoRef.current
      const canvas = canvasRef.current
      if (!video || !canvas || !video.videoWidth || !video.videoHeight) return

      if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth
      if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight

      const ctx = canvas.getContext("2d")
      if (!ctx) return

      try {
        const results = landmarker.detectForVideo(video, performance.now())
        lastCaptureRef.current = {
          results,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          mirrored: true,
        }
        drawFaceMesh(ctx, results, canvas.width, canvas.height, { mirror: true })
      } catch (err) {
        // A transient throw (stream re-initialising) is fine to skip, but a
        // PERSISTENT one is exactly how the live mesh silently never draws — surface
        // it once so it isn't invisible (e.g. a clashing second MediaPipe graph).
        if (!errorLogged) {
          errorLogged = true
          console.error("[face-mesh] detectForVideo failed; live mesh will not draw:", err)
        }
      }
    }

    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [videoRef, landmarker, active])

  return { canvasRef, lastCaptureRef }
}
